import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import '../styles/landing.css';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="landing-container">
      {/* Background Elements */}
      <div className="bg-orb bg-orb-1"></div>
      <div className="bg-orb bg-orb-2"></div>
      <div className="noise-overlay"></div>

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_ahorro-constante/artifacts/if3q1xmz_QUITAMELO_PORQUE_ME_LO_GASTO_6.png" 
            alt="Quitamelo Logo" 
            className="logo"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-neon hover:text-neon"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <section className="section" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div className="privacy-content">
          <h1 className="text-4xl font-bold text-neon mb-6">Aviso de Privacidad</h1>
          
          <div className="text-gray-300 space-y-6" style={{ lineHeight: '1.8' }}>
            <p className="text-sm text-gray-400">
              <strong>Última actualización:</strong> Enero 2026
            </p>

            <p>
              En cumplimiento con lo dispuesto por la Ley Federal de Protección de Datos Personales en Posesión 
              de los Particulares (en adelante la "Ley") y su Reglamento, <strong>Quítamelo Porque Me Lo Gasto</strong> 
              (en adelante "Quítamelo", "nosotros" o "el Responsable"), pone a su disposición el presente Aviso 
              de Privacidad.
            </p>

            {/* Identidad del Responsable */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">1. Identidad y Domicilio del Responsable</h2>
              <p>
                <strong>Razón Social:</strong> Quítamelo Porque Me Lo Gasto<br />
                <strong>Domicilio:</strong> México<br />
                <strong>Correo electrónico:</strong> info@quitameloporquemelogasto.com.mx<br />
                <strong>Sitio web:</strong> www.quitameloporquemelogasto.com.mx
              </p>
            </div>

            {/* Datos Personales */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">2. Datos Personales que Recabamos</h2>
              <p>
                Para las finalidades señaladas en el presente Aviso de Privacidad, podemos recabar sus datos 
                personales de distintas formas: cuando usted nos los proporciona directamente a través de nuestro 
                sitio web, aplicaciones móviles (iOS y Android), o cuando utiliza nuestros servicios.
              </p>
              <p className="mt-3"><strong>Los datos personales que recabamos incluyen:</strong></p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Datos de identificación:</strong> nombre completo, correo electrónico, número de teléfono</li>
                <li><strong>Datos financieros:</strong> ingresos mensuales, metas de ahorro, hábitos de gasto, 
                historial de transacciones (cuando utiliza nuestros servicios de ahorro e inversión)</li>
                <li><strong>Datos de navegación:</strong> dirección IP, tipo de navegador, páginas visitadas, 
                tiempo de permanencia en el sitio</li>
                <li><strong>Datos del dispositivo móvil:</strong> identificador único del dispositivo, sistema operativo, 
                versión de la aplicación (cuando usa nuestras apps iOS o Android)</li>
                <li><strong>Datos de geolocalización:</strong> ubicación aproximada (solo si otorga permiso en la aplicación móvil)</li>
              </ul>
              <p className="mt-3 text-yellow-400">
                <strong>Datos Sensibles:</strong> No recabamos datos personales sensibles como origen racial o étnico, 
                estado de salud, información genética, creencias religiosas, filosóficas o morales, afiliación 
                sindical, opiniones políticas o preferencia sexual.
              </p>
            </div>

            {/* Finalidades */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">3. Finalidades del Tratamiento de Datos</h2>
              
              <h3 className="text-xl font-semibold text-white mt-4 mb-2">Finalidades Primarias (Necesarias):</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Proveer los servicios de planificación financiera, ahorro e inversión que solicite</li>
                <li>Procesar y dar seguimiento a sus solicitudes de información sobre productos financieros</li>
                <li>Contactarlo para agendar citas y brindar asesoría financiera personalizada</li>
                <li>Gestionar su cuenta de usuario en nuestro sitio web y aplicaciones móviles</li>
                <li>Realizar análisis de su perfil financiero y recomendarle productos adecuados</li>
                <li>Cumplir con obligaciones legales y regulatorias aplicables</li>
                <li>Enviar notificaciones sobre el estado de sus ahorros e inversiones</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-4 mb-2">Finalidades Secundarias (Opcionales):</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Enviarle comunicaciones de marketing sobre nuevos productos y servicios financieros</li>
                <li>Realizar estudios de mercado y análisis estadísticos</li>
                <li>Mejorar nuestros servicios, sitio web y aplicaciones móviles</li>
                <li>Personalizar su experiencia de usuario</li>
                <li>Enviarle tips y contenido educativo sobre finanzas personales</li>
              </ul>

              <p className="mt-4 text-gray-400 text-sm">
                Si usted no desea que sus datos personales sean tratados para las finalidades secundarias, 
                puede manifestarlo enviando un correo a: <strong className="text-neon">info@quitameloporquemelogasto.com.mx</strong>
              </p>
            </div>

            {/* Transferencias */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">4. Transferencias de Datos Personales</h2>
              <p>
                Sus datos personales pueden ser transferidos y tratados dentro y fuera del país, por personas 
                distintas a Quítamelo. En ese sentido, su información puede ser compartida con:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong>Instituciones financieras aliadas</strong> (como Allianz México) para el procesamiento 
                de productos de ahorro e inversión que usted solicite</li>
                <li><strong>Proveedores de servicios tecnológicos</strong> que nos ayudan a operar nuestro sitio 
                web y aplicaciones móviles (servicios de hosting, análisis de datos, envío de emails)</li>
                <li><strong>Proveedores de servicios de pago</strong> para procesar transacciones financieras</li>
                <li><strong>Autoridades competentes</strong> cuando sea requerido por ley</li>
              </ul>
              <p className="mt-3">
                Estas transferencias son necesarias para la prestación de nuestros servicios. Todas las empresas 
                con las que compartimos su información están obligadas a cumplir con las medidas de seguridad 
                y confidencialidad establecidas por la Ley.
              </p>
            </div>

            {/* Derechos ARCO */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">5. Derechos ARCO y Revocación del Consentimiento</h2>
              <p>
                Usted tiene derecho a conocer qué datos personales tenemos de usted, para qué los utilizamos y 
                las condiciones del uso que les damos (Acceso). Asimismo, es su derecho solicitar la corrección 
                de su información personal en caso de que esté desactualizada, sea inexacta o incompleta 
                (Rectificación); que la eliminemos de nuestros registros o bases de datos cuando considere que 
                la misma no está siendo utilizada adecuadamente (Cancelación); así como oponerse al uso de sus 
                datos personales para fines específicos (Oposición). Estos derechos se conocen como derechos ARCO.
              </p>

              <h3 className="text-xl font-semibold text-white mt-4 mb-2">¿Cómo ejercer sus derechos ARCO?</h3>
              <p>
                Para ejercer cualquiera de los derechos ARCO, usted deberá presentar una solicitud a través de:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong>Correo electrónico:</strong> info@quitameloporquemelogasto.com.mx</li>
                <li><strong>Asunto:</strong> "Solicitud de Ejercicio de Derechos ARCO"</li>
              </ul>

              <p className="mt-3"><strong>Su solicitud deberá contener:</strong></p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Nombre completo del titular</li>
                <li>Domicilio o correo electrónico para recibir respuesta</li>
                <li>Documentos que acrediten su identidad (INE/IFE o pasaporte)</li>
                <li>Descripción clara y precisa de los datos sobre los que busca ejercer alguno de los derechos</li>
                <li>Cualquier documento o información que facilite la localización de sus datos</li>
              </ul>

              <p className="mt-3 text-yellow-400">
                <strong>Tiempo de respuesta:</strong> Le responderemos en un plazo máximo de 20 días hábiles, 
                contados desde la fecha en que recibimos su solicitud, sobre la procedencia de la misma. Si 
                resulta procedente, se hará efectiva dentro de los 15 días hábiles siguientes.
              </p>
            </div>

            {/* Revocación del Consentimiento */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">6. Revocación del Consentimiento</h2>
              <p>
                Usted puede revocar el consentimiento que nos ha otorgado para el tratamiento de sus datos 
                personales. Sin embargo, es importante que tenga en cuenta que no en todos los casos podremos 
                atender su solicitud o concluir el uso de forma inmediata, ya que es posible que por alguna 
                obligación legal requiramos seguir tratando sus datos personales.
              </p>
              <p className="mt-3">
                Para revocar su consentimiento, deberá seguir el mismo procedimiento que para ejercer sus 
                derechos ARCO, enviando un correo a: <strong className="text-neon">info@quitameloporquemelogasto.com.mx</strong>
              </p>
            </div>

            {/* Cookies y Tecnologías */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">7. Uso de Cookies y Tecnologías de Rastreo</h2>
              <p>
                Le informamos que en nuestra página web y aplicaciones móviles utilizamos cookies, web beacons 
                y otras tecnologías de rastreo a través de las cuales es posible monitorear su comportamiento 
                como usuario de internet y de nuestras aplicaciones, así como brindarle un mejor servicio y 
                experiencia al navegar.
              </p>
              <p className="mt-3"><strong>Los datos personales que obtenemos de estas tecnologías son:</strong></p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Identificadores, nombre de usuario y contraseñas de sesión</li>
                <li>Región en la que se encuentra el usuario</li>
                <li>Tipo de navegador y dispositivo utilizado</li>
                <li>Fecha y hora del inicio y final de una sesión</li>
                <li>Páginas web visitadas y búsquedas realizadas</li>
                <li>Interacción con funcionalidades de la aplicación móvil</li>
              </ul>
              <p className="mt-3">
                Estas cookies pueden ser deshabilitadas desde la configuración de su navegador. Para saber cómo 
                hacerlo, consulte la ayuda de su navegador. En las aplicaciones móviles, puede controlar los 
                permisos desde la configuración de su dispositivo.
              </p>
            </div>

            {/* Aplicaciones Móviles */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">8. Aplicaciones Móviles (iOS y Android)</h2>
              <p>
                Nuestras aplicaciones móviles disponibles en <strong>App Store</strong> y <strong>Google Play Store</strong> 
                pueden solicitar acceso a las siguientes funcionalidades de su dispositivo:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong>Notificaciones Push:</strong> Para enviarle recordatorios sobre sus metas de ahorro 
                y actualizaciones importantes</li>
                <li><strong>Cámara:</strong> Para escanear documentos o comprobantes (si esta funcionalidad está disponible)</li>
                <li><strong>Ubicación:</strong> Para ofrecerle servicios personalizados según su región (solo con su autorización explícita)</li>
                <li><strong>Almacenamiento:</strong> Para guardar información de su sesión y mejorar el rendimiento de la app</li>
              </ul>
              <p className="mt-3 text-yellow-400">
                <strong>Importante:</strong> Usted puede revocar estos permisos en cualquier momento desde la 
                configuración de privacidad de su dispositivo móvil. Algunos permisos son necesarios para el 
                funcionamiento básico de la aplicación.
              </p>
            </div>

            {/* Seguridad */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">9. Medidas de Seguridad</h2>
              <p>
                Quítamelo ha implementado medidas de seguridad administrativas, técnicas y físicas para proteger 
                sus datos personales, mismas que igualmente exigimos sean cumplidas por los proveedores de 
                servicios que contratamos. Estas medidas incluyen:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Encriptación de datos en tránsito y en reposo</li>
                <li>Autenticación de dos factores para acceso a cuentas</li>
                <li>Servidores seguros con certificados SSL</li>
                <li>Auditorías periódicas de seguridad</li>
                <li>Capacitación constante a nuestro personal sobre protección de datos</li>
                <li>Controles de acceso estrictos a bases de datos</li>
              </ul>
              <p className="mt-3 text-gray-400">
                Sin embargo, ningún sistema de seguridad es infalible. Le recomendamos utilizar contraseñas 
                seguras y no compartir su información de acceso con terceros.
              </p>
            </div>

            {/* Menores de Edad */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">10. Menores de Edad</h2>
              <p>
                Nuestros servicios están dirigidos a personas mayores de 18 años. No recabamos intencionalmente 
                datos personales de menores de edad. Si usted es padre o tutor y cree que su hijo menor de edad 
                nos ha proporcionado datos personales, por favor contáctenos inmediatamente.
              </p>
            </div>

            {/* Cambios al Aviso */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">11. Cambios al Aviso de Privacidad</h2>
              <p>
                El presente Aviso de Privacidad puede sufrir modificaciones, cambios o actualizaciones derivadas 
                de nuevos requerimientos legales, de nuestras propias necesidades por los productos o servicios 
                que ofrecemos, de nuestras prácticas de privacidad o por otras causas.
              </p>
              <p className="mt-3">
                Nos comprometemos a mantenerlo informado sobre los cambios que pueda sufrir el presente Aviso 
                de Privacidad, a través de:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Publicación en nuestro sitio web: www.quitameloporquemelogasto.com.mx</li>
                <li>Notificación por correo electrónico a la dirección que nos proporcionó</li>
                <li>Notificación en nuestras aplicaciones móviles</li>
              </ul>
            </div>

            {/* Autoridad de Protección de Datos */}
            <div>
              <h2 className="text-2xl font-bold text-neon mb-4">12. Autoridad de Protección de Datos</h2>
              <p>
                Si usted considera que su derecho a la protección de datos personales ha sido lesionado por 
                alguna conducta u omisión de nuestra parte, o presume alguna violación a las disposiciones 
                previstas en la Ley Federal de Protección de Datos Personales en Posesión de los Particulares, 
                su Reglamento y demás ordenamientos aplicables, podrá interponer su inconformidad o denuncia 
                ante el Instituto Nacional de Transparencia, Acceso a la Información y Protección de Datos 
                Personales (INAI).
              </p>
              <p className="mt-3">
                <strong>INAI - Instituto Nacional de Transparencia, Acceso a la Información y Protección de 
                Datos Personales</strong><br />
                Sitio web: <a href="https://home.inai.org.mx" target="_blank" rel="noopener noreferrer" 
                className="text-neon hover:underline">https://home.inai.org.mx</a><br />
                Teléfono: 800 835 43 24
              </p>
            </div>

            {/* Consentimiento */}
            <div className="mt-8 p-6 bg-gray-900 border border-neon rounded-lg">
              <h2 className="text-2xl font-bold text-neon mb-4">13. Consentimiento</h2>
              <p className="mb-4">
                Al proporcionar sus datos personales a través de nuestro sitio web, aplicaciones móviles, o 
                por cualquier otro medio, usted consiente y acepta que sus datos personales sean tratados 
                conforme a los términos y condiciones del presente Aviso de Privacidad.
              </p>
              <p className="text-yellow-400 font-semibold">
                Si usted no está de acuerdo con los términos expuestos en este Aviso de Privacidad, le pedimos 
                que se abstenga de proporcionarnos sus datos personales y de utilizar nuestros servicios.
              </p>
            </div>

            {/* Contacto */}
            <div className="mt-8 p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <h3 className="text-xl font-bold text-white mb-3">Contacto para Asuntos de Privacidad</h3>
              <p>
                Si tiene alguna duda o comentario sobre el presente Aviso de Privacidad, puede contactarnos en:
              </p>
              <p className="mt-3">
                <strong>Email:</strong> <a href="mailto:info@quitameloporquemelogasto.com.mx" 
                className="text-neon hover:underline">info@quitameloporquemelogasto.com.mx</a><br />
                <strong>Sitio web:</strong> <a href="https://www.quitameloporquemelogasto.com.mx" 
                target="_blank" rel="noopener noreferrer" className="text-neon hover:underline">
                www.quitameloporquemelogasto.com.mx</a>
              </p>
            </div>

            <div className="mt-8 text-center text-sm text-gray-500">
              <p>© 2026 Quítamelo Porque Me Lo Gasto. Todos los derechos reservados.</p>
              <p className="mt-2">Este Aviso de Privacidad cumple con la Ley Federal de Protección de Datos 
              Personales en Posesión de los Particulares</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="footer">
        <div className="footer-content">
          <p className="text-gray-400 text-sm">
            <button onClick={() => navigate('/')} className="text-neon hover:underline">
              Volver al inicio
            </button>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
